import { AssetType, CardType, ExtraDataType, SortDir } from '../../api/models/Shared.types';
import { CardSizeInfo } from '../cms/CardSizeInfo';
import { CardDataSource } from '../cms/CardDataSource';
import { CmsRibbon } from '../../components/cms/CmsRibbon';
import { Theme } from '../../config/theme';
import { Backend } from '../../api/Backend';
import { ChannelModel } from '../../api/EpgApi.types';
import { IFavoriteElementV1, IRibbon2Asset, IRibbon2Detail, IScheduleTime } from '../../api/models/AndromeTV.types';
import { EpgApiV5 } from '../../api/EpgApiV5';
import { IFiltersInfo } from './EpgListDataSource';

const _constants = {
  firstLoadAmount: 6,
  loadAmount: 3,
};

export interface ListRibbon2Detail extends IRibbon2Detail {
  tags?: string[];
  published: IScheduleTime;
}

export interface ListRibbon2Asset extends IRibbon2Asset {
  asset: ListRibbon2Detail;
}

export class ListRibbonDataSource {
  public backend: Backend;
  private channel: ChannelModel;
  private from: Date;
  private until: Date;
  private replay: boolean;
  private filter: IFiltersInfo | undefined; // filterState that was applied to page and this ribbon
  private readonly properties: string[];
  private readonly channels: ChannelModel[];
  private readonly isChannel: boolean;
  private hasNextPage: boolean;
  private readonly sizeInfo: CardSizeInfo;
  private cards: CardDataSource[];
  private loadedIdx: number;
  private loaded: boolean;
  public title: string; // accessed directly by cmsRibbon
  public favorite: Promise<IFavoriteElementV1 | undefined> | undefined; // accessed directly by cmsRibbon

  constructor(
    backend: Backend,
    channel: ChannelModel,
    from: Date,
    until: Date,
    replay = false,
    filter: IFiltersInfo | undefined,
    properties: string[],
    channels: ChannelModel[],
    title: string,
    isChannel = false
  ) {
    this.backend = backend;
    this.channel = channel;
    this.from = from;
    this.until = until;
    this.replay = replay;
    this.filter = filter;
    this.properties = properties;
    this.channels = channels;
    this.title = title;
    this.isChannel = isChannel;

    this.hasNextPage = true;
    this.sizeInfo = new CardSizeInfo();
    this.sizeInfo.setToListPageSettings();
    this.cards = [];

    this.loadedIdx = 0;
    this.loaded = false;
  }

  load() {
    return EpgApiV5.channelLineupRibbon(
      this.backend,
      this.channel.channelId,
      this.from,
      this.until,
      this.filter?.categories,
      this.filter?.accessibility,
      this.replay ? SortDir.Desc : SortDir.Asc,
      ExtraDataType.MINI
    ).then(result => {
      if (!result.items) {
        return [];
      }

      if (this.isChannel) {
        this.favorite = this.backend.cachedFavoriteApi.getFavorite(this.backend, {
          id: this.channel.channelId,
          type: AssetType.CHANNEL,
        });
      }

      const noUndef = result.items.filter(el => el);
      this.sizeInfo.updateWithContainer(
        {
          type: CardType.Landscape,
          nextPage: false,
          items: noUndef,
        },
        this.properties
      );

      this.cards = noUndef.map(
        item => new CardDataSource(this.backend, item, this.sizeInfo, this.properties, this.channels)
      );
      this.hasNextPage = false;
      this.loaded = true;
      return this.cards;
    });
  }

  loadNext() {
    let numberToLoad: number;
    if (this.loadedIdx === 0) {
      numberToLoad = _constants.firstLoadAmount;
    } else {
      numberToLoad = _constants.loadAmount;
    }
    if (this.loadedIdx >= this.cards.length) {
      return Promise.resolve([]);
    } else {
      return new Promise(resolve => {
        const result = this.cards.slice(this.loadedIdx, this.loadedIdx + numberToLoad);
        this.loadedIdx += numberToLoad;
        resolve(result);
      });
    }
  }

  hasNext() {
    return this.loadedIdx < this.cards.length || this.hasNextPage;
  }

  get isLoaded() {
    return this.loaded;
  }

  isEmpty() {
    return this.cards.length === 0;
  }

  totalHeight() {
    return Theme.Fonts.Ribbon.Title.lineHeight + this.sizeInfo.height;
  }

  height() {
    return this.totalHeight();
  }

  componentDescription() {
    return {
      focusable: true,
      type: CmsRibbon,
      data: this,
      h: this.totalHeight(),
    };
  }
}
